import type { FC } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { MdOutlineConfirmationNumber } from 'react-icons/md';
import { useTheme } from '@mui/material';

import type { DashboardWidgetProps } from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import WidgetFrame from 'src/components/dashboard/DashboardPage/components/WidgetFrame';
import { getRelativeValue } from 'src/components/dashboard/DashboardPage/helper';
import { useExportOptions } from 'src/components/dashboard/DashboardPage/widgets/DetractorTicketsWidget/hooks';
import DoughnutChart from 'src/components/followUp/common/BaseWidgets/DoughnutChart/DoughnutChart';
import type { AggregateList } from 'src/components/followUp/common/types';
import NoResultsToShowAdjustFiltersWarning from 'src/components/followUp/common/warning/NoResultsToShowAdjustFiltersWarning';
import WidgetFooterLegend from 'src/components/followUp/common/WidgetFooterLegend/WidgetFooterLegend';
import WidgetHeaderWithPill from 'src/components/followUp/common/WidgetHeaderWithPill/WidgetHeaderWithPill';
import useGenerateWidgetEndpoint from 'src/components/followUp/hooks/useGenerateWidgetEndpoint';
import useWidgetQuery from 'src/components/followUp/hooks/useWidgetQuery';

const MyFollowUpStateWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const ariaId = useId();
  const { t } = useTranslation('followUp');

  const { colors } = useTheme();
  const endpoint = useGenerateWidgetEndpoint(
    apiPaths.WIDGET_DATA.MYFOLLOWUP_STATE,
    config.id,
  );

  const { data, isFetching, errorMsg, refetch, queryFilters } =
    useWidgetQuery<AggregateList>(endpoint);

  const labels = [
    {
      name: t('statistics.detractorTicketsWidget.labels.open'),
      color: colors.secondary[600],
    },
    {
      color: colors.secondary[400],
      name: t('statistics.detractorTicketsWidget.labels.inProgress'),
    },
    {
      color: colors.secondary[50],
      name: t('statistics.detractorTicketsWidget.labels.closed'),
    },
    {
      color: colors.secondary[900],
      name: t('statistics.detractorTicketsWidget.labels.overDue'),
    },
  ];

  const exportOptions = useExportOptions({
    data: data ?? null,
    comparisonData: null,
    labels,
    widgetName: config.name,
    queryFilters,
  });

  return (
    <WidgetFrame
      ariaId={ariaId}
      refetch={refetch}
      loading={isFetching}
      error={errorMsg}
      exportOptions={exportOptions}
      header={
        <WidgetHeaderWithPill
          ariaId={ariaId}
          pillProps={{
            label: (data?.totalObjects ?? 0).toLocaleString(),
            icon: <MdOutlineConfirmationNumber />,
            color: 'primary',
          }}
          title={config.name}
        />
      }
      footer={
        data?.totalObjects === 0 ? (
          <NoResultsToShowAdjustFiltersWarning />
        ) : (
          <WidgetFooterLegend labels={labels} />
        )
      }
    >
      {
        <DoughnutChart
          data={
            data?.results.map((result) => ({
              absoluteValue: result.value,
              relativeValue: getRelativeValue(result.value, data.totalObjects),
              label: t(
                `statistics.detractorTicketsWidget.labels.${result.label}`,
              ),
            })) ?? []
          }
          labels={labels}
        />
      }
    </WidgetFrame>
  );
};

export default MyFollowUpStateWidget;
